import React, { useState, useContext } from 'react';
import './LifelineEventEditModal.css';
import { AppContext } from '../../../../appContext';
import { DashboardContext } from '../../DashboardContext';

interface ILifelineEventEditModalProps {
    eventId: string;
    eventData: {
        Datetime: string;
        Event: string;
        Description?: string;
        Level: 1 | 2;
        Good: boolean;
    };
    onClose: () => void;
    onSave: (eventId: string, data: any) => void;
    onDelete: (eventId: string) => void;
}

export const LifelineEventEditModal = ({ eventId, eventData, onClose, onSave, onDelete }: ILifelineEventEditModalProps) => {
    const { translate } = useContext(AppContext);
    const { selectedClient } = useContext(DashboardContext);

    const formatDateForInput = (dateString: string): string => {
        // Если дата пустая, null или содержит только пробелы - возвращаем пустую строку
        if (!dateString || !dateString.trim()) {
            return '';
        }
        
        // Convert from DD.MM.YYYY to YYYY-MM-DD
        if (dateString.includes('.')) {
            const [day, month, year] = dateString.split('.');
            
            // Если год не указан или содержит только "-", возвращаем пустую строку (событие без даты)
            if (!year || year === '--' || year === '---' || year.trim() === '') {
                return '';
            }
            
            // Если год есть, но день или месяц отсутствуют - заменяем их на '01'
            const normalizedDay = (!day || day === '--') ? '01' : day;
            const normalizedMonth = (!month || month === '--') ? '01' : month;
            
            return `${year}-${normalizedMonth.padStart(2, '0')}-${normalizedDay.padStart(2, '0')}`;
        }
        return dateString;
    };

    const formatDateForServer = (dateString: string): string => {
        if (!dateString) return '';
        
        // dateString is in YYYY-MM-DD format from HTML input
        const [year, month, day] = dateString.split('-');
        
        // Convert to DD.MM.YYYY format expected by server
        return `${day.padStart(2, '0')}.${month.padStart(2, '0')}.${year}`;
    };

    const validateDate = (dateString: string): boolean => {
        if (!dateString) return false;
        const date = new Date(dateString);
        return date instanceof Date && !isNaN(date.getTime());
    };

    const [datetime, setDatetime] = useState(formatDateForInput(eventData.Datetime));
    const [event, setEvent] = useState(eventData.Event);
    const [description, setDescription] = useState(eventData.Description || '');
    const [good, setGood] = useState(eventData.Good);
    const [level, setLevel] = useState<1 | 2>(eventData.Level);
    const [isLoading, setIsLoading] = useState(false);
    const [dateError, setDateError] = useState(false);
    
    // Определяем, является ли это событием без даты
    const isEventWithoutDate = !eventData.Datetime || eventData.Datetime.trim() === '';

    const handleSave = async () => {
        // Сбрасываем ошибку даты
        setDateError(false);
        
        if (!event.trim()) {
            alert(translate('admin_fill_required'));
            return;
        }

        // Дата ОБЯЗАТЕЛЬНА для всех событий при сохранении
        if (!datetime.trim()) {
            setDateError(true);
            return;
        }

        // Проверяем формат даты
        if (!validateDate(datetime)) {
            setDateError(true);
            return;
        }

        const formattedData = {
            Datetime: formatDateForServer(datetime),
            Event: event,
            Description: description,
            Level: level,
            Good: good
        };

        setIsLoading(true);
        try {
            await onSave(eventId, formattedData);
            onClose();
        } catch (error) {
            console.error('Error saving event:', error);
            alert(translate('lifeline_error_save'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!confirm(translate('lifeline_confirm_delete'))) {
            return;
        }

        setIsLoading(true);
        try {
            await onDelete(eventId);
            onClose();
        } catch (error) {
            console.error('Error deleting event:', error);
            alert(translate('lifeline_error_delete'));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="LifelineEventEditModal_overlay" onClick={onClose}>
            <div className="LifelineEventEditModal_content" onClick={(e) => e.stopPropagation()}>
                <div className="LifelineEventEditModal_header">
                    <h3>{translate('lifeline_edit_event')}</h3>
                    <button className="LifelineEventEditModal_close" onClick={onClose}>×</button>
                </div>

                <div className="LifelineEventEditModal_form">
                    <div className="LifelineEventEditModal_field">
                        <label>
                            {translate('lifeline_event_date')}
                            {isEventWithoutDate && <span style={{color: '#E23424', fontStyle: 'italic'}}> ({translate('required') || 'обязательно'})</span>}:
                        </label>
                        <input
                            type="date"
                            value={datetime}
                            onChange={(e) => {
                                setDatetime(e.target.value);
                                setDateError(false); // Сбрасываем ошибку при изменении
                            }}
                            disabled={isLoading}
                            style={{
                                borderColor: dateError ? '#E23424' : undefined,
                                borderWidth: dateError ? '2px' : undefined
                            }}
                        />
                        {dateError && (
                            <div style={{color: '#E23424', fontSize: '12px', marginTop: '4px'}}>
                                {translate('lifeline_date_required') || 'Укажите дату события'}
                            </div>
                        )}
                    </div>

                    <div className="LifelineEventEditModal_field">
                        <label>{translate('lifeline_event_title')}:</label>
                        <textarea
                            value={event}
                            onChange={(e) => setEvent(e.target.value)}
                            placeholder={translate('lifeline_event_title')}
                            disabled={isLoading}
                            rows={3}
                        />
                    </div>

                    <div className="LifelineEventEditModal_field">
                        <label>{translate('lifeline_event_description')}:</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder={translate('lifeline_event_description')}
                            disabled={isLoading}
                            rows={4}
                        />
                    </div>

                    <div className="LifelineEventEditModal_field">
                        <label>{translate('lifeline_event_type')}:</label>
                        <div className="LifelineEventEditModal_radio_group">
                            <label className="LifelineEventEditModal_radio_label">
                                <input
                                    type="radio"
                                    name="eventType"
                                    checked={good}
                                    onChange={() => setGood(true)}
                                    disabled={isLoading}
                                />
                                {translate('lifeline_positive_event')}
                            </label>
                            <label className="LifelineEventEditModal_radio_label">
                                <input
                                    type="radio"
                                    name="eventType"
                                    checked={!good}
                                    onChange={() => setGood(false)}
                                    disabled={isLoading}
                                />
                                {translate('lifeline_negative_event')}
                            </label>
                        </div>
                    </div>

                    <div className="LifelineEventEditModal_field">
                        <label className="LifelineEventEditModal_checkbox_label">
                            <input
                                type="checkbox"
                                checked={level === 2}
                                onChange={(e) => setLevel(e.target.checked ? 2 : 1)}
                                disabled={isLoading}
                            />
                            {translate('lifeline_important_event')}
                        </label>
                    </div>
                </div>

                <div className="LifelineEventEditModal_actions">
                    <button 
                        className="LifelineEventEditModal_delete"
                        onClick={handleDelete}
                        disabled={isLoading}
                    >
                        {translate('lifeline_delete_event')}
                    </button>
                    <div className="LifelineEventEditModal_right_actions">
                        <button 
                            className="LifelineEventEditModal_cancel"
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            {translate('lifeline_cancel')}
                        </button>
                        <button 
                            className="LifelineEventEditModal_save"
                            onClick={handleSave}
                            disabled={isLoading}
                        >
                            {isLoading ? translate('lifeline_loading_save') : translate('lifeline_save_changes')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};